import { useEffect, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import {
  AggregateField,
  AggregateTarget,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import {
  calcProgressLineXPercent,
  calcTodayXPercent,
  ProgressLineInput,
} from '../CellRenderer/progressLine'
import { GanttDisplayUnit } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { useDatePositions } from '../CellRenderer/GanttBackground'
import { Progress } from '../../../../../../../../pages/ProjectPlanNew/gridOptions/cumulation'
import objects from '../../../../../../../../../utils/objects'
import DateVO from '../../../../../../../../../vo/DateVO'
import { WbsItemStatus } from '../../wbsItemStatus'
import { isGanttSummaryType } from '../ganttUtil'
import { GanttRowItem } from '../type'

export type ProgressLinePoint = {
  x: number
  y: number
  progressRate: number
  scheduledProgressRate?: number
  isFullyComplete: boolean
  isSummary: boolean
}

type ProgressLineResult = {
  points: ProgressLinePoint[]
  firstRowTop: number
  lastRowBottom: number
  todayX: number | null
}

const EMPTY_RESULT: ProgressLineResult = {
  points: [],
  firstRowTop: 0,
  lastRowBottom: 0,
  todayX: null,
}

const calcPoints = <TData>(
  gridApi: GridApi<TData>,
  timeScale: CalendarDateVO[],
  ganttColumnWidth: number,
  aggregateTarget: AggregateTarget,
  aggregateField: AggregateField,
  dataPath: string,
  datePositions: { x: number; width: number; date: CalendarDateVO }[],
  getGanttRowItem?: (data: TData) => GanttRowItem | undefined
): ProgressLineResult => {
  const today = new DateVO(new Date())
  const now = today.toDate()
  const endOfToday = CalendarDateVO.of(today.addDays(1).toDate())
  const todayXPercent = calcTodayXPercent(datePositions, endOfToday)
  if (todayXPercent == null) return EMPTY_RESULT

  const todayX = (todayXPercent / 100) * ganttColumnWidth
  const prefix = dataPath ? `${dataPath}.` : ''
  const points: ProgressLinePoint[] = []
  let firstRowTop = 0
  let lastRowBottom = 0

  gridApi.forEachNodeAfterFilterAndSort(node => {
    const {
      displayed,
      data,
      allLeafChildren,
      rowTop: rawRowTop,
      rowHeight: rawRowHeight,
    } = node
    if (!displayed || !data) return

    // getGanttRowItem が提供されている場合はそちらを使う
    let status: string | undefined
    let wbsItemType: GanttRowItem['wbsItemType']
    let scheduledDate: { startDate?: string; endDate?: string } | undefined
    let progressRate = 0
    let scheduledProgressRate: number | undefined

    if (getGanttRowItem) {
      const rowItem = getGanttRowItem(data)
      if (!rowItem) return

      status = rowItem.status
      wbsItemType = rowItem.wbsItemType
      scheduledDate = rowItem.scheduledDate
      progressRate = rowItem.progressRate ?? 0
    } else {
      const body = objects.getValue(data, 'body')
      if (!body) return

      const wbsItem = objects.getValue(data, `${prefix}wbsItem`)
      if (!wbsItem) return

      const cumulation = objects.getValue(data, `${prefix}cumulation`)
      if (cumulation) {
        const progress = new Progress(
          cumulation,
          aggregateTarget,
          aggregateField
        )
        progressRate = progress.progressRate ?? 0
        scheduledProgressRate = progress.scheduledProgressRate
      }

      status = wbsItem.status
      wbsItemType = wbsItem.wbsItemType
      scheduledDate = wbsItem.scheduledDate
    }

    const hasChildren = (allLeafChildren && allLeafChildren.length > 0) || false

    const rawStart = scheduledDate?.startDate
    const rawEnd = scheduledDate?.endDate
    const input: ProgressLineInput = {
      status: status as WbsItemStatus | undefined,
      wbsItemType,
      scheduledStartDate: rawStart ? new DateVO(rawStart).toDate() : undefined,
      scheduledEndDate: rawEnd ? new DateVO(rawEnd).toDate() : undefined,
      progressRate,
      hasChildren,
    }

    const rawXPercent = calcProgressLineXPercent(
      input,
      timeScale,
      now,
      todayXPercent
    )
    const x = (rawXPercent / 100) * ganttColumnWidth

    const rowTop = rawRowTop ?? 0
    const rowHeight = rawRowHeight ?? 32
    const y = rowTop + rowHeight / 2

    if (points.length === 0) {
      firstRowTop = rowTop
    }
    lastRowBottom = rowTop + rowHeight

    const isFullyComplete =
      progressRate >= 1 ||
      status === WbsItemStatus.DONE ||
      status === WbsItemStatus.DISCARD
    const isSummary = isGanttSummaryType(wbsItemType)

    points.push({
      x,
      y,
      progressRate,
      scheduledProgressRate,
      isFullyComplete,
      isSummary,
    })
  })

  return { points, firstRowTop, lastRowBottom, todayX }
}

export const useProgressLinePoints = <TData>(
  gridApi: GridApi<TData> | undefined,
  timeScale: CalendarDateVO[],
  ganttColumnWidth: number,
  aggregateTarget: AggregateTarget,
  aggregateField: AggregateField,
  dataPath: string,
  unit: GanttDisplayUnit,
  getGanttRowItem?: (data: TData) => GanttRowItem | undefined
): ProgressLineResult => {
  const datePositions = useDatePositions({ timeScale, unit })
  const [result, setResult] = useState<ProgressLineResult>(EMPTY_RESULT)

  useEffect(() => {
    if (!gridApi || timeScale.length === 0 || ganttColumnWidth <= 0) {
      setResult(EMPTY_RESULT)
      return
    }

    const update = () => {
      setResult(
        calcPoints(
          gridApi,
          timeScale,
          ganttColumnWidth,
          aggregateTarget,
          aggregateField,
          dataPath,
          datePositions,
          getGanttRowItem
        )
      )
    }

    update()
    gridApi.addEventListener('modelUpdated', update)
    return () => gridApi.removeEventListener('modelUpdated', update)
  }, [
    datePositions,
    gridApi,
    timeScale,
    ganttColumnWidth,
    aggregateTarget,
    aggregateField,
    dataPath,
    getGanttRowItem,
  ])

  return result
}
